/**
 * Shared types for the canonical platform-preview primitive set.
 *
 * Mora-native platform presentation shells — designed to communicate the shape
 * of a post without claiming provider-rendered parity. Provider capability and
 * publish-payload truth live in the adapters and platform-API docs, not here.
 * Used by:
 *
 *   • Onboarding completion "wow" moment
 *   • Calendar post detail sheet
 *   • Repurpose workbench (alongside the editorial v2 set)
 *   • Plans / scheduled-post viewer
 *   • Any "see what this will look like" surface
 *
 * NOT to be confused with `src/components/create/v2/sections/post-preview/` —
 * those are intentional editorial workbench tools that show the format SIGNAL
 * (LinkedIn fold, X 280-char counter, IG carousel slide index) for the user
 * to author against. This set shows an editorial approximation of the post
 * surface and must never be used as the source of truth for provider support
 * or delivery behavior.
 */

import type { ReactNode } from 'react';

import { getPlatformLabel } from '@/lib/platforms/identity';

export type PlatformPreviewKind =
  | 'instagram-post'
  | 'instagram-reel'
  | 'instagram-story'
  | 'tiktok'
  | 'tiktok-photo'
  | 'facebook-post'
  | 'facebook-reel'
  | 'facebook-story'
  | 'linkedin-post'
  | 'x-post'
  | 'youtube-short'
  | 'youtube-landscape'
  | 'pinterest-pin'
  | 'threads-post';

/**
 * Map a short platform identifier (as stored on `posts.platforms`) to the
 * canonical `PlatformPreviewKind` used by the preview primitives. Accepts
 * common variants ('twitter' → 'x-post', 'youtube' → 'youtube-short', etc.).
 * Returns `null` if the platform isn't supported by the presentation-shell set yet —
 * caller should fall back to a text-only view in that case.
 */
export function platformToKind(platform: string | null | undefined): PlatformPreviewKind | null {
  if (!platform) return null;
  const p = platform.toLowerCase().trim();
  if (p === 'instagram' || p === 'instagram-post' || p === 'ig') return 'instagram-post';
  if (p === 'instagram-reel' || p === 'reel' || p === 'reels') return 'instagram-reel';
  if (p === 'instagram-story' || p === 'story' || p === 'stories') return 'instagram-story';
  if (p === 'tiktok' || p === 'tt') return 'tiktok';
  if (p === 'facebook' || p === 'facebook-post' || p === 'fb') return 'facebook-post';
  if (p === 'linkedin' || p === 'linkedin-post' || p === 'li') return 'linkedin-post';
  if (p === 'x' || p === 'twitter' || p === 'x-post') return 'x-post';
  if (p === 'youtube' || p === 'youtube-short' || p === 'shorts' || p === 'yt')
    return 'youtube-short';
  if (p === 'pinterest' || p === 'pinterest-pin' || p === 'pin') return 'pinterest-pin';
  if (p === 'threads' || p === 'threads-post') return 'threads-post';
  return null;
}

/** Human-readable label for a platform string (e.g. for switcher tabs). */
export function platformLabel(platform: string | null | undefined): string {
  if (!platform) return 'Platform';
  const p = platform.toLowerCase().trim();
  if (!p) return 'Platform';
  if (p.startsWith('instagram')) return 'Instagram';
  if (p === 'tiktok' || p === 'tt') return 'TikTok';
  if (p.startsWith('facebook') || p === 'fb') return 'Facebook';
  if (p.startsWith('linkedin') || p === 'li') return 'LinkedIn';
  if (p === 'x' || p === 'twitter' || p === 'x-post') return 'X';
  if (p.startsWith('youtube') || p === 'shorts' || p === 'yt') return 'YouTube';
  if (p.startsWith('pinterest') || p === 'pin') return 'Pinterest';
  if (p.startsWith('threads')) return 'Threads';
  return getPlatformLabel(platform);
}

export interface PlatformPreviewData {
  /** Brand display name shown as the post author. Required. */
  brandName: string;
  /** Handle used as the inline username in the caption. Defaults from brandName. */
  username?: string;
  /** Brand color (hex) — used for the avatar disc when no avatar image provided. */
  brandColor?: string;
  /** Optional avatar image URL — overrides brandColor + initials. */
  avatarUrl?: string;
  /** Verified-checkmark next to the brand name (Instagram + X + TikTok + Facebook). */
  isVerified?: boolean;
  /** The caption / body text. */
  caption?: string;
  /** Title — used by YouTube + Pinterest. */
  title?: string;
  /** Single image URL — post + story + pin + reel cover. */
  imageUrl?: string | null;
  /** Multiple images — Instagram carousel + reel cover stack. */
  imageUrls?: string[];
  /**
   * Intrinsic aspect ratios for `imageUrls`, in the same order. Used only
   * when there is no explicit destination crop command, so a native carousel
   * changes its preview frame as the viewer advances through mixed-ratio
   * children. Unknown children are `null` rather than guessed.
   */
  imageAspectRatios?: Array<number | string | null>;
  /**
   * Ordered mixed-media children for providers that support image/video
   * carousels. This is an editor presentation input only; publish support is
   * still decided by the platform readiness and adapter contracts.
   */
  mediaItems?: Array<{
    url: string;
    kind: 'image' | 'video';
    videoPoster?: string | null;
  }>;
  /**
   * Controlled active child for the editor's native-carousel preview. Keeping
   * this index at the PostBody seam lets the editor size its stage from the
   * same child the presentation shell is showing. Standalone shells may omit
   * it and keep their own local carousel state.
   */
  carouselActiveIndex?: number;
  onCarouselActiveIndexChange?: (index: number) => void;
  /**
   * Video URL — when set, the media region renders an autoplaying muted
   * looping `<video>` instead of an `<img>`. Mirrors how Instagram, TikTok,
   * YouTube Shorts, Facebook, LinkedIn, X, and Threads display in-feed video.
   * `videoPoster` (optional) sets the still-frame shown before/while loading.
   */
  videoUrl?: string | null;
  videoPoster?: string | null;
  /**
   * Aspect ratio override for the media region. Accepts:
   *  - a shorthand string ('1:1', '4:5', '9:16', '1.91:1', '2:3', '16:9')
   *  - a number (width / height)
   *  - a legacy Tailwind aspect-class fragment, tolerated but no longer emitted
   *
   * NOTE: no arbitrary-value class example here, on purpose. Tailwind scans
   * comments, so an example becomes a real rule in the shipped stylesheet —
   * which is how the class-based predecessor appeared to work for a handful of
   * ratios and silently failed for every other one.
   *
   * To be clear about what this does and does not buy: omitting the example
   * here does NOT remove those utilities from the build — the same literals
   * appear in ~15 production files and the style guide, so they compile
   * regardless. This is hygiene so that a COMMENT is never the only reason a
   * rule exists; it is not a mitigation, and the phantom-rule hazard is closed
   * by the ratio travelling as an inline style, not by this.
   * When omitted the presentation shell uses the source/child ratio when the
   * editor has it, otherwise the platform-default ratio (per
   * `docs/platform-apis/MEDIA-MATRIX.md` D4). This is presentation geometry;
   * the shared dispatcher materializes an explicit image crop command before
   * delivery and rejects explicit video crop because Mora has no transcoder.
   */
  aspectRatio?: number | string | null;
  /**
   * Focal point of the cover-crop, 0..1 (0.5 = centre). Lets the user reposition
   * which part of the image the frame keeps — matches the publish-time crop.
   */
  focalX?: number;
  focalY?: number;
  /** Zoom ≥ 1 (1 = fill) for the cover-crop. */
  zoom?: number;
  /**
   * Actual publication time only. Missing, invalid, or future values render
   * the neutral "Preview" label so drafts never look as if they already ran.
   */
  timestamp?: Date | string;
  /** Optional real engagement metrics. Missing values stay hidden in previews. */
  engagement?: {
    likes?: number;
    comments?: number;
    shares?: number;
    views?: number;
    saves?: number;
  };
  /** Loading state — shows a skeleton scaffold rather than empty placeholders. */
  isLoading?: boolean;
  /** Empty media hint shown inside the platform media frame. */
  emptyHint?: string;
  /**
   * Optional node layered over the media region only (above the image/video,
   * inside MediaSlot). The Composer uses it for the on-demand safe-zone
   * overlay; composed with any native media overlay (e.g. IG carousel
   * controls). `pointer-events-none` overlays recommended.
   */
  mediaOverlay?: ReactNode;
}

export interface PlatformPreviewBaseProps extends PlatformPreviewData {
  className?: string;
}

export type PlatformPreviewSizeMode = 'natural' | 'fill';

/**
 * The disc colour a replica falls back to when the caller has no brand colour
 * — a near-black that reads as deliberate rather than as a missing value.
 *
 * It was fourteen copies of the same literal across this directory, one per
 * replica's `brandColor = '#1A1A1A'` default, plus one more in
 * `onboarding-v2/onboarding-state.ts` (left alone: that one is a real seeded
 * BRAND value a user can change, not a render-time fallback — the same hex
 * meaning two different things).
 *
 * Not a platform skin role and deliberately not in `platform-tokens.ts`: this
 * stands in for the USER's brand, which no platform has an opinion about.
 */
export const DEFAULT_PREVIEW_BRAND_COLOR = '#1A1A1A';

export function platformNaturalMaxWidth(kind: PlatformPreviewKind): number {
  switch (kind) {
    // Full-bleed vertical players share one footprint — instagram-reel and
    // instagram-story sit here (not at the instagram feed-card width) since
    // Task A5 gave Story its own dedicated 9:16 presentation shell (was grouped with
    // instagram-post pre-A5, back when Story reused the feed-card structure).
    case 'tiktok':
    case 'tiktok-photo':
    case 'youtube-short':
    case 'youtube-landscape':
    case 'instagram-reel':
    case 'instagram-story':
      return 300;
    case 'pinterest-pin':
      return 260;
    case 'instagram-post':
      return 420;
    case 'facebook-post':
    case 'facebook-reel':
    case 'facebook-story':
    case 'linkedin-post':
    case 'x-post':
    case 'threads-post':
      return 480;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/**
 * The platform chrome that sits outside its media frame, in the published-post
 * presentation shells. The post-inspector stage uses this to reserve only the
 * space a given shell needs before scaling it to the available height.
 *
 * Full-bleed viewers deliberately return zero: their platform controls live
 * over the 9:16 media rather than adding to its height. The values are modest
 * buffers, not fixed card heights, so captions can still grow and the stage
 * can scroll when needed.
 */
export function platformPreviewChromeHeight(kind: PlatformPreviewKind): number {
  switch (kind) {
    case 'instagram-reel':
    case 'instagram-story':
    case 'tiktok':
    case 'tiktok-photo':
    case 'youtube-short':
    case 'youtube-landscape':
      return 0;
    case 'pinterest-pin':
      return 72;
    case 'instagram-post':
      return 140;
    case 'facebook-post':
    case 'facebook-reel':
    case 'facebook-story':
    case 'linkedin-post':
      return 168;
    case 'x-post':
      return 148;
    case 'threads-post':
      return 132;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/**
 * True when this kind's presentation shell renders a full-bleed vertical stage.
 *
 * Safe-zone overlays are 9:16 fractions and only mean anything over a 9:16
 * stage. `hasChrome` in the placement matrix is NOT a substitute: `facebook-reel`
 * and `facebook-story` are both `hasChrome: true` yet route to `FacebookPost`
 * — a feed card sized by the media's own ratio — so gating on `hasChrome` alone
 * painted 9:16 bands over a 1.78 landscape box. That was invisible while the
 * media box had no height; now it is just wrong on screen.
 */
export function rendersFullBleedStage(kind: PlatformPreviewKind): boolean {
  switch (kind) {
    case 'instagram-reel':
    case 'instagram-story':
    case 'tiktok':
    case 'youtube-short':
    case 'youtube-landscape':
      return true;
    // NOTE: `facebook-reel` and `facebook-story` belong in THIS group even
    // though the placement matrix marks them `hasChrome: true` — they route to
    // the FacebookPost feed card, not a 9:16 stage. That mismatch is the whole
    // reason this predicate exists instead of reading the matrix flag.
    // 'tiktok-photo' deliberately sits here, NOT with 'tiktok' above — a
    // TikTok photo carousel accepts 1:1/9:16/16:9 (unlike the video row,
    // locked to exactly 9:16), so it can't claim a fixed vertical stage.
    case 'tiktok-photo':
    case 'instagram-post':
    case 'facebook-post':
    case 'facebook-reel':
    case 'facebook-story':
    case 'linkedin-post':
    case 'x-post':
    case 'pinterest-pin':
    case 'threads-post':
      return false;
    default: {
      // Exhaustive, like its neighbours above. Without this a new vertical
      // presentation shell (PlatformPreview already flags a dedicated FacebookReel as
      // pending) would silently fall through to `false` and ship with its
      // safe zones missing, with no typecheck signal.
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

/** Compute initials from a brand name — `Mora Test Store` → `MT`. */
export function initialsOf(name: string): string {
  const cleaned = (name || '').trim();
  if (!cleaned) return 'M';
  const parts = cleaned.split(/\s+/).filter(Boolean).slice(0, 2);
  const fromParts = parts.map((s) => s[0]?.toUpperCase() ?? '').join('');
  return fromParts || cleaned.slice(0, 2).toUpperCase();
}

/** Brightness check so avatar text contrasts against the brand-color disc. */
export function isDarkColor(hex: string): boolean {
  const cleaned = hex?.replace('#', '') || '';
  if (cleaned.length !== 6) return true;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 < 165;
}

/** Truthful publication label for a preview: a real past time or neutral Preview. */
export function previewTimestampLabel(input?: Date | string): string {
  if (!input) return 'Preview';
  const ts = typeof input === 'string' ? new Date(input) : input;
  const diff = Date.now() - ts.getTime();
  if (Number.isNaN(diff) || diff < 0) return 'Preview';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} ${min === 1 ? 'minute' : 'minutes'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  const wk = Math.floor(day / 7);
  return `${wk}w`;
}

// Per-platform default aspect ratios now live in the canonical placement
// matrix (`src/lib/platform-matrix/placement-matrix.ts`,
// `getPlacementByPreviewKind(kind).recommendedRatio`). The old
// PLATFORM_DEFAULT_ASPECT record was dead (no importers) and was removed.

/**
 * Facebook's feed fold, commonly cited around 477 characters before
 * "See more". Shared by the organic feed shell and the Sponsored ad shell so
 * the two truncate at the same point — an ad that folds differently from a
 * post is a preview that lies.
 */
export const FACEBOOK_FEED_FOLD_LENGTH = 477;

/**
 * Resolve any aspect-ratio shape (shorthand, number, or a legacy Tailwind
 * fragment) into a plain NUMBER, for an inline `aspect-ratio` style.
 *
 * It deliberately returns a number and never a class name. Building a utility
 * class from a runtime ratio is what caused BUG-PREVIEW-ORIGINAL-ASPECT-COLLAPSE-001:
 * Tailwind only emits arbitrary-value utilities whose exact text it finds while
 * scanning source, so a class synthesised from a probed float
 * (naturalWidth / naturalHeight) had no CSS rule at all, the media box lost its
 * aspect-ratio, and — since every child is absolutely positioned — collapsed to
 * height 0 and the image vanished. An inline style carries any value, so that
 * entire class of defect is unreachable from here.
 *
 *   resolveAspectRatio('1:1', 1)        -> 1
 *   resolveAspectRatio('4:5', 1)        -> 0.8
 *   resolveAspectRatio(1.7777777, 1)    -> 1.7777777
 *   resolveAspectRatio('0:5', 1)        -> 1  (fallback; a zero side is not a ratio)
 *   resolveAspectRatio(undefined, 1)    -> 1  (the fallback)
 */
export function resolveAspectRatio(
  value: number | string | null | undefined,
  fallback: number,
): number {
  if (value == null || value === '') return fallback;
  if (typeof value === 'number') {
    // Deliberately NOT gamut-clamped. A number here comes from decoding a real
    // image (naturalWidth / naturalHeight), so an extreme value is the truth
    // about that file, and "the preview never lies" outranks tidiness. The
    // string path below is clamped because strings come from persisted data,
    // which this column has a documented history of corrupting.
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }
  let trimmed = value.trim();
  // Legacy tolerance: persisted data and older callers may still hold a
  // Tailwind fragment. Strip the prefix and re-parse whatever is inside, which
  // covers both the bracketed form and Tailwind v4's bare `aspect-3/2`.
  if (trimmed.startsWith('aspect-')) {
    if (trimmed === 'aspect-square') return 1;
    trimmed = trimmed.slice('aspect-'.length);
    if (trimmed.startsWith('[')) {
      // FIRST `]`, not last: a multi-class string like `aspect-[4/5] w-[200px]`
      // would otherwise slice through to the trailing bracket and yield junk.
      const close = trimmed.indexOf(']');
      if (close === -1) return fallback;
      trimmed = trimmed.slice(1, close);
    }
  }
  if (trimmed === 'square') return 1;
  const m = trimmed.match(/^(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)$/);
  if (m) {
    const w = Number(m[1]);
    const h = Number(m[2]);
    // A zero on either side is not a ratio; it would compute to an invalid
    // aspect-ratio and collapse the box just like the original bug.
    return inGamut(w > 0 && h > 0 ? w / h : Number.NaN, fallback);
  }
  return inGamut(Number(trimmed), fallback);
}

/**
 * Reject ratios so extreme the media is effectively invisible anyway.
 *
 * `Number('1e2')` is 100 and `Number('0x10')` is 16 — both finite and positive,
 * so a corrupt persisted string would sail through and render a 100:1 sliver
 * about four pixels tall. That is a silent disappearance wearing a different
 * hat, which is precisely what this whole change exists to stop. 0.2–5.0 spans
 * every row in the placement matrix (widest 1.91:1, tallest 9:16) with headroom.
 */
const MIN_USABLE_RATIO = 0.2;
const MAX_USABLE_RATIO = 5;
function inGamut(n: number, fallback: number): number {
  return Number.isFinite(n) && n >= MIN_USABLE_RATIO && n <= MAX_USABLE_RATIO ? n : fallback;
}

/**
 * An explicit crop is uniform intent for the destination. Otherwise a
 * carousel's active child owns the frame: LinkedIn and Facebook may accept
 * mixed source ratios, so using child zero's ratio for every slide lies about
 * the native upload. Callers can omit/leave an entry null while its dimensions
 * are still being probed; the presentation shell then falls back to its normal
 * default.
 */
export function resolveCarouselAspectRatio(
  explicitAspectRatio: number | string | null | undefined,
  childAspectRatios: Array<number | string | null> | undefined,
  activeIndex: number,
): number | string | null | undefined {
  return explicitAspectRatio ?? childAspectRatios?.[activeIndex] ?? undefined;
}

/** Compact engagement number — 1234 → "1.2K", 1500000 → "1.5M". */
export function compactNumber(n: number | undefined): string {
  if (n == null) return '0';
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0).replace(/\.0$/, '')}K`;
  return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
}
